/**
 * Google Analytics 4 (GA4) Integration
 * Tracks user behavior, traffic sources, and key events
 */

import ReactGA from 'react-ga4';

// Configuration
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';
const IS_PRODUCTION = import.meta.env.PROD;
const DEBUG_MODE = import.meta.env.DEV;

/**
 * Initialize Google Analytics
 * Call this once when the app loads
 */
export const initGA = (): void => {
  if (!GA_MEASUREMENT_ID) {
    if (DEBUG_MODE) {
      console.debug('Google Analytics: Measurement ID not configured (skipping)');
    }
    return;
  }

  try {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      testMode: !IS_PRODUCTION,
      gaOptions: {
        debug_mode: DEBUG_MODE,
      },
    });

    if (DEBUG_MODE) {
      console.log('Google Analytics initialized:', GA_MEASUREMENT_ID);
    }
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Track page view
 * Call this on route changes
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });

    if (DEBUG_MODE) {
      console.log('GA PageView:', path);
    }
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

/**
 * Track custom events
 * Generic event tracking function
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
): void => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });

    if (DEBUG_MODE) {
      console.log('GA Event:', { category, action, label, value });
    }
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

// ============================================================================
// CUSTOM EVENTS FOR SCAN2PRINT
// ============================================================================

/**
 * Track QR code scan
 */
export const trackQRScan = (shopId: string, shopName: string): void => {
  trackEvent('QR_Code', 'Scan', `Shop: ${shopName}`, parseInt(shopId) || 0);
};

/**
 * Track document upload
 */
export const trackDocumentUpload = (
  shopId: string,
  fileCount: number,
  totalSizeMB: number
): void => {
  trackEvent('Documents', 'Upload_Started', `Shop ${shopId}`, fileCount);

  // Track file size separately
  ReactGA.event({
    category: 'Documents',
    action: 'Upload_Size',
    label: `${totalSizeMB.toFixed(2)} MB`,
    value: Math.round(totalSizeMB),
  });
};

/**
 * Track successful document upload
 */
export const trackDocumentUploadSuccess = (
  shopId: string,
  fileCount: number,
  uploadDurationSeconds: number
): void => {
  trackEvent(
    'Documents',
    'Upload_Success',
    `Shop ${shopId}`,
    fileCount
  );

  // Track upload performance
  ReactGA.event({
    category: 'Performance',
    action: 'Upload_Duration',
    label: `${uploadDurationSeconds}s`,
    value: uploadDurationSeconds,
  });
};

/**
 * Track document upload failure
 */
export const trackDocumentUploadError = (
  shopId: string,
  errorType: string
): void => {
  trackEvent('Documents', 'Upload_Error', errorType);
};

/**
 * Track document print action (shopkeeper marks as printed)
 */
export const trackDocumentPrint = (
  documentId: string,
  shopId: string,
  pageCount: number
): void => {
  trackEvent('Documents', 'Printed', `Shop ${shopId}`, pageCount);
};

/**
 * Track print confirmation (sender confirms receipt)
 */
export const trackPrintConfirmation = (
  documentId: string,
  revenue: number,
  printType: 'color' | 'bw'
): void => {
  // Track confirmation event
  trackEvent('Revenue', 'Print_Confirmed', printType, Math.round(revenue));

  // E-commerce event for revenue tracking
  ReactGA.event('purchase', {
    transaction_id: documentId,
    value: revenue,
    currency: 'INR',
    items: [{
      item_id: documentId,
      item_name: `Print Job - ${printType}`,
      item_category: 'Printing',
      price: revenue,
      quantity: 1,
    }],
  });
};

/**
 * Track shop profile view
 */
export const trackShopView = (shopId: string, shopName: string, source: string): void => {
  trackEvent('Shop', 'Profile_View', `${shopName} (from ${source})`);
};

/**
 * Track shop search
 */
export const trackShopSearch = (query: string, resultCount: number): void => {
  trackEvent('Shop', 'Search', query, resultCount);
};

/**
 * Track user authentication
 */
export const trackLogin = (userType: 'sender' | 'shopkeeper'): void => {
  trackEvent('Authentication', 'Login', userType);
};

export const trackSignup = (userType: 'sender' | 'shopkeeper'): void => {
  trackEvent('Authentication', 'Signup', userType);
};

export const trackLogout = (): void => {
  trackEvent('Authentication', 'Logout');
};

/**
 * Track WebSocket connection status
 */
export const trackWebSocketConnect = (shopId: string): void => {
  trackEvent('WebSocket', 'Connected', `Shop ${shopId}`);
};

export const trackWebSocketDisconnect = (shopId: string, reason: string): void => {
  trackEvent('WebSocket', 'Disconnected', reason);
};

/**
 * Track errors and exceptions
 */
export const trackError = (errorType: string, errorMessage: string, fatal: boolean = false): void => {
  ReactGA.event('exception', {
    description: `${errorType}: ${errorMessage}`,
    fatal,
  });
};

/**
 * Track timing events (performance monitoring)
 */
export const trackTiming = (
  category: string,
  variable: string,
  value: number,
  label?: string
): void => {
  if (!GA_MEASUREMENT_ID) return;

  ReactGA.event({
    category: 'Timing',
    action: category,
    label: `${variable}${label ? ` - ${label}` : ''}`,
    value: Math.round(value),
  });
};

/**
 * Track user engagement time on page
 */
export const trackEngagement = (pageName: string, timeSeconds: number): void => {
  trackTiming('Engagement', 'Time_On_Page', timeSeconds, pageName);
};

/**
 * Set user properties
 */
export const setUserProperties = (properties: {
  userType?: 'sender' | 'shopkeeper' | 'admin';
  userId?: string;
  shopId?: string;
  city?: string;
}): void => {
  if (!GA_MEASUREMENT_ID) return;

  try {
    if (properties.userId) {
      ReactGA.set({ userId: properties.userId });
    }

    // Set custom dimensions
    Object.entries(properties).forEach(([key, value]) => {
      if (value) {
        ReactGA.gtag('set', 'user_properties', {
          [key]: value,
        });
      }
    });
  } catch (error) {
    console.error('Failed to set user properties:', error);
  }
};

/**
 * Track outbound links
 */
export const trackOutboundLink = (url: string, label?: string): void => {
  trackEvent('Outbound', 'Click', label || url);
};

/**
 * Track ad clicks (for future ad monetization)
 */
export const trackAdClick = (adId: string, placement: string): void => {
  trackEvent('Ads', 'Click', placement);
};

export const trackAdImpression = (adId: string, placement: string): void => {
  trackEvent('Ads', 'Impression', placement);
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackQRScan,
  trackDocumentUpload,
  trackDocumentUploadSuccess,
  trackDocumentUploadError,
  trackDocumentPrint,
  trackPrintConfirmation,
  trackShopView,
  trackShopSearch,
  trackLogin,
  trackSignup,
  trackLogout,
  trackWebSocketConnect,
  trackWebSocketDisconnect,
  trackError,
  trackTiming,
  trackEngagement,
  setUserProperties,
  trackOutboundLink,
  trackAdClick,
  trackAdImpression,
};